import {AgGridReact} from 'ag-grid-react';
import {AllCommunityModule, ColDef, GridOptions, ModuleRegistry, themeQuartz} from 'ag-grid-community';
import React, {useState, useRef, useMemo, useCallback} from 'react';

export interface GridButton {
  displayText: string;
  styling: 'primary' | 'secondary';
  onClick: () => void;
}

interface DataGridProps extends Omit<GridOptions, 'gridRef'> {
  buttons?: GridButton[];
}

ModuleRegistry.registerModules([AllCommunityModule]);

/**
 * Component used to display a grid of provided data.
 * @param rowData Data to display in the grid
 * @param colDefs List of definitions for each column
 * @param defaultColDef Default column properties
 * @param onCellValueChanged Optional function that runs each time a cell value has changed
 * @param buttons Optional array of button objects to display above the grid
 * @returns Data grid component
 */
const DataGrid = (props: DataGridProps) => {
  const gridRef: any = useRef();
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);
  
  // Add custom styles for pagination fix
  const paginationStyles = useMemo(() => `
    .ag-paging-panel .ag-paging-page-summary-panel {
      white-space: nowrap !important;
      min-width: fit-content !important;
      flex-shrink: 0 !important;
    }
  `, []);
  
  const defaultOnFirstDataRendered = useCallback(() => {
    gridRef.current.api.sizeColumnsToFit();
  }, []);

  const {
    pagination = true,
    paginationAutoPageSize = true,
    onFirstDataRendered = defaultOnFirstDataRendered,
    buttons = [],
    ...otherProps
  } = props;

  if (buttons.length > 0) return (
    <>
      <style>{paginationStyles}</style>
      <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr'}}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', border: '1px solid #ccc', borderBottom: 'none', padding: '1rem', borderTopLeftRadius: '4px', borderTopRightRadius: '4px'}}>
          {buttons.map((button, index) => (
            <button 
              key={index}
              className={`btn btn--${button.styling}`}
              onClick={button.onClick}
            >
              {button.displayText}
            </button>
          ))}
        </div>
        <div style={gridStyle}>
          <AgGridReact
            ref={gridRef}
            onFirstDataRendered={onFirstDataRendered}
            paginationAutoPageSize={paginationAutoPageSize}
            pagination={pagination}
            {...otherProps}
          />
        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{paginationStyles}</style>
      <div style={gridStyle}>
        <AgGridReact
          ref={gridRef}
          onFirstDataRendered={onFirstDataRendered}
          paginationAutoPageSize={paginationAutoPageSize}
          pagination={pagination}
          {...otherProps}
        />
      </div>
    </>
  );
};

export default DataGrid;